import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Filter, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';

export const Shop = () => {
  const { t, i18n } = useTranslation();
  const { products, fetchProducts, loading } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');
  const [showFilters, setShowFilters] = useState(false);

  const lang = i18n.language;

  const categoryTaglines = {
    en: {
      all: "Let fear change sides. Premium self-defense tools for women ready to fight back.",
      taser: "Let fear change sides. Your safety comes first.",
      alarm: "You are not alone. Make noise. Be heard.",
      kubaton: "Built to protect, designed to empower.",
      hammer: "Small tool. Big confidence.",
      spray: "Your safety, in your hands.",
      accessories: "Discreet protection. Everyday carry.",
      pack: "Complete protection. One bundle. Best value."
    },
    es: {
      all: "Que el miedo cambie de bando. Herramientas de defensa personal premium para mujeres listas para luchar.",
      taser: "Que el miedo cambie de bando. Tu seguridad es lo primero.",
      alarm: "No estás sola. Haz ruido. Hazte escuchar.",
      kubaton: "Hecho para proteger, diseñado para empoderar.",
      hammer: "Herramienta pequeña. Gran confianza.",
      spray: "Tu seguridad, en tus manos.",
      accessories: "Protección discreta. Para llevar siempre.",
      pack: "Protección completa. Un pack. Mejor precio."
    },
    fr: {
      all: "Que la peur change de camp. Outils d'autodéfense premium pour les femmes prêtes à riposter.",
      taser: "Que la peur change de camp. Votre sécurité d'abord.",
      alarm: "Vous n'êtes pas seule. Faites du bruit. Faites-vous entendre.",
      kubaton: "Conçu pour protéger, créé pour autonomiser.",
      hammer: "Petit outil. Grande confiance.",
      spray: "Votre sécurité, entre vos mains.",
      accessories: "Protection discrète. À emporter partout.",
      pack: "Protection complète. Un pack. Meilleur prix."
    }
  };

  const categories = [
    { key: 'all', label: t('shop.all') },
    { key: 'taser', label: t('shop.tasers') },
    { key: 'alarm', label: t('shop.alarms') },
    { key: 'kubaton', label: t('shop.kubatons') },
    { key: 'spray', label: t('shop.sprays') },
    { key: 'accessories', label: t('shop.accessories') },
    { key: 'pack', label: t('shop.packs') },
  ];

  useEffect(() => {
    const category = searchParams.get('category');
    if (category) {
      setActiveCategory(category);
      fetchProducts({ category });
    } else {
      fetchProducts();
    }
  }, [searchParams, fetchProducts]);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    if (category === 'all') {
      setSearchParams({});
      fetchProducts();
    } else {
      setSearchParams({ category });
      fetchProducts({ category });
    }
    setShowFilters(false);
  };

  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen pt-20" data-testid="shop-page">
      {/* Header */}
      <div className="bg-[#0a0a0a] py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-4xl sm:text-5xl text-white font-bold text-center">
            {t('shop.title')}
          </h1>
          <p className="text-[#A3A3A3] text-center mt-4 max-w-2xl mx-auto">
            {categoryTaglines[lang]?.[activeCategory] || categoryTaglines.en[activeCategory] || categoryTaglines.en.all}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Filters */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 bg-[#1A1A1A] border border-[#333333] p-6">
              <h3 className="font-heading text-white font-bold uppercase tracking-wider mb-6">
                Categories
              </h3>
              <div className="flex flex-col gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.key}
                    onClick={() => handleCategoryChange(cat.key)}
                    className={`text-left px-4 py-3 transition-all ${
                      activeCategory === cat.key
                        ? 'bg-[#FF0099] text-white'
                        : 'text-[#A3A3A3] hover:text-white hover:bg-[#333333]'
                    }`}
                    data-testid={`filter-${cat.key}`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Mobile Filter Button */}
          <div className="lg:hidden flex items-center justify-between mb-4">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary flex items-center gap-2"
              data-testid="mobile-filter-btn"
            >
              <Filter className="w-4 h-4" />
              Filters
            </Button>
            <span className="text-[#A3A3A3]">
              {filteredProducts.length} products
            </span>
          </div>

          {/* Mobile Filters Dropdown */}
          {showFilters && (
            <div className="lg:hidden bg-[#1A1A1A] border border-[#333333] p-4 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-white font-bold uppercase tracking-wider">
                  Categories
                </h3>
                <button onClick={() => setShowFilters(false)}>
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.key}
                    onClick={() => handleCategoryChange(cat.key)}
                    className={`px-4 py-2 transition-all ${
                      activeCategory === cat.key
                        ? 'bg-[#FF0099] text-white'
                        : 'bg-[#333333] text-[#A3A3A3] hover:text-white'
                    }`}
                    data-testid={`mobile-filter-${cat.key}`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-[#1A1A1A] animate-pulse aspect-square" />
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-[#A3A3A3] text-lg">No products found in this category.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
